import { VIDEO_MAX_DURATION, VIDEO_MIN_DURATION } from '../config/env';

export type VideoDurationReason = 'too_short' | 'too_long' | 'unreadable';

export type VideoDurationValidation =
  | { ok: true; duration: number }
  | { ok: false; reason: VideoDurationReason; title: string; message: string };

const RANGE_MESSAGE = `Please select a video between ${VIDEO_MIN_DURATION} and ${VIDEO_MAX_DURATION} seconds.`;

export interface VideoDurationOptions {
  min?: number;
  max?: number;
}

/**
 * ImagePicker reports duration in seconds on iOS/web and often milliseconds on Android.
 */
export function normalizeVideoDuration(rawDuration?: number | null): number {
  if (rawDuration == null || !Number.isFinite(rawDuration) || rawDuration <= 0) {
    return 0;
  }
  return rawDuration > 1000 ? rawDuration / 1000 : rawDuration;
}

export function validateVideoDuration(
  durationSeconds: number,
  options?: VideoDurationOptions
): VideoDurationValidation {
  const min = options?.min ?? VIDEO_MIN_DURATION;
  const max = options?.max ?? VIDEO_MAX_DURATION;
  const rangeMessage = `Please select a video between ${min} and ${max} seconds.`;

  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return {
      ok: false,
      reason: 'unreadable',
      title: 'Unable to read video',
      message: 'This video file could not be processed. Please try a different video.',
    };
  }

  if (durationSeconds < min) {
    return {
      ok: false,
      reason: 'too_short',
      title: 'Video is too short',
      message: rangeMessage,
    };
  }

  if (durationSeconds > max) {
    return {
      ok: false,
      reason: 'too_long',
      title: 'Video is too long',
      message: rangeMessage,
    };
  }

  return { ok: true, duration: durationSeconds };
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) {
    return '0 B';
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDurationSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '';
  }
  return `${Math.round(seconds)}s`;
}
